import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter, getTitle } from './lib';
import { IDevicesAvgDischargeTimeIntervalStats } from '../../../../services/shared/device/batteryDevicesService';

interface IProps {
  data: IDevicesAvgDischargeTimeIntervalStats,
  isLoading?: boolean
}

interface DeviceAvgDischargeTimeChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof DeviceAvgDischargeTimeChartData,
    description: 'Devices Avg Discharge Time Count',
    colour: '#1e4263ff'
  },
];

export function DeviceAvgDischargeTimeChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: DeviceAvgDischargeTimeChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-1',
        count: props.data.hour0To1,
      },
      {
        bucket: '1-2',
        count: props.data.hour1To2,
      },
      {
        bucket: '2-3',
        count: props.data.hour2To3,
      },
      {
        bucket: '3-4',
        count: props.data.hour3To4,
      },
      {
        bucket: '4-5',
        count: props.data.hour4To5,
      },
      {
        bucket: '5-6',
        count: props.data.hour5To6,
      },
      {
        bucket: '6-7',
        count: props.data.hour6To7,
      },
      {
        bucket: '7-8',
        count: props.data.hour7To8,
      },
      {
        bucket: '8-9',
        count: props.data.hour8To9,
      },
      {
        bucket: '9-10',
        count: props.data.hour9To10,
      },
      {
        bucket: '>10',
        count: props.data.hour10AndAbove,
      }
    ];
    return data;
  }, [props.data]);

  const chartProps: StackedColumnsCategoryChartProps<DeviceAvgDischargeTimeChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: getTitle(t, 'DEVICE_AVG_DISCHARGE_TIME_CHART_TITLE', true),
    cypressId: 'device-avg-discharge-time-chart',
    className: 'device-avg-discharge-time-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 70,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('DEVICE_AVG_DISCHARGE_TIME_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_DEVICES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t, true)
  };

  return (
    <Card noPadding={false} className="device-avg-discharge-time-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
